import React from 'react';
import ProductCard from './ProductCard';
import Pagination from './Pagination';
import StateBlock from './StateBlock';
import { ProductGridSkeleton } from './Skeletons';
import useCartActions from '../../hooks/useCartActions';
import useWishlistActions from '../../hooks/useWishlistActions';
import { useShopUI } from '../../context/ShopUIContext';

/**
 * Renders a set of products as either a responsive grid or a
 * horizontally scrolling rail, with the loading, empty and error
 * states all handled in one place.
 *
 * This replaces near-identical markup and handlers that were pasted
 * into Home, HotProducts, NewArrival, AlsoLike and Search.
 */
const ProductCollection = ({
  products = [],
  loading = false,
  error = null,
  onRetry,
  layout = 'grid',
  // Pins the grid to a fixed column count (landing page shows two
  // rows of four); left unset, the grid auto-fills to the viewport.
  columns,
  // Caps how many products render, for feeds that return more than
  // the section is meant to show.
  limit,
  skeletonCount = 8,
  emptyTitle = 'No products found',
  emptyMessage = 'Try adjusting your filters, or browse the full collection.',
  emptyAction,
  showActions = true,
  currentPage,
  lastPage,
  onPageChange,
  // The first screenful should not be lazy-loaded — it is the LCP image.
  eagerCount = 4,
}) => {
  const { openLogin, openCart } = useShopUI();

  const { addToCart, pendingVariantId } = useCartActions({
    onCartOpen: openCart,
    onLoginRequired: openLogin,
  });

  const { isWishlisted, toggleWishlist } = useWishlistActions({
    onLoginRequired: openLogin,
  });

  const containerClass =
    layout === 'rail'
      ? 'product-rail'
      : `product-grid${columns ? ` product-grid--${columns}up` : ''}`;

  const visibleProducts = limit ? products.slice(0, limit) : products;

  if (loading) {
    return <ProductGridSkeleton count={skeletonCount} variant={layout} columns={columns} />;
  }

  if (error) {
    return (
      <StateBlock
        variant="error"
        title="We couldn't load these products"
        message="There was a problem reaching our store. Please check your connection and try again."
        actionLabel={onRetry ? 'Retry' : undefined}
        onAction={onRetry}
      />
    );
  }

  if (visibleProducts.length === 0) {
    return (
      <StateBlock title={emptyTitle} message={emptyMessage}>
        {emptyAction}
      </StateBlock>
    );
  }

  return (
    <>
      <div className={containerClass}>
        {visibleProducts.map((product, index) => (
          <ProductCard
            key={product.id ?? product.product_code}
            product={product}
            showActions={showActions}
            isWishlisted={isWishlisted(product.product_code)}
            onToggleWishlist={toggleWishlist}
            onAddToCart={addToCart}
            isAddingToCart={pendingVariantId != null && pendingVariantId === product?.variant?.id}
            imageLoading={index < eagerCount ? 'eager' : 'lazy'}
          />
        ))}
      </div>

      {onPageChange && (
        <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={onPageChange} />
      )}
    </>
  );
};

export default ProductCollection;
